// src/components/module/compras/PurchaseOrderDetails.tsx
// Rediseño corporativo estilo Notion/Linear
import React, { useEffect, useState, useContext, useRef } from "react";
import { toast } from '../../../utils/toast';
import {
  Box,
  Button,
  Paper,
  CircularProgress,
  Typography,
  Stack,
  Fade,
  alpha,
} from "@mui/material";
import { Save, ArrowBack } from "@mui/icons-material";
import { DarkModeContext } from "../../../context/darkModeContext";
import { useMaintenanceApi } from "../../../services/api/maintenanceApi";
import { useSucursalesApi } from "../../../services/api/sucursalesApi";
import { useImpuestosApi } from "../../../services/api/impuestosApi";
import {
  useArticulosApi,
  ArticuloSearchResult,
} from "../../../services/api/articulosApi";
import type {
  ArticuloCompra,
  SucursalItem,
  GenericItem,
  PurchaseOrderDetailsProps,
  PriceUpdate,
  OrderData,
} from "../../types/purchaseOrder.types";
import type { ImpuestoItem } from "../../../services/api/impuestosApi";

import HeaderSection from "./components/HeaderSection";
import SucursalSection from "./components/SucursalSection";
import ArticulosGrid from "./components/ArticulosGrid";
import PriceUpdateDialog from "./components/PriceUpdateDialog";
import NotificationSnackbars from "./components/NotificationSnackbars";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const P = {
  light: { bg: "#FFFFFF", bgS: "#F7F7F5", text: "#37352F", textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7", accent: "#2383E2" },
  dark: { bg: "#191919", bgS: "#202020", text: "#E8E8E6", textS: "#9B9A97", textT: "#6B6B6B", border: "#333333", accent: "#529CCA" },
};

const PurchaseOrderDetails = ({
  idCompra,
  orderDetails,
  onSave,
}: PurchaseOrderDetailsProps): JSX.Element => {
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;
  const navigate = useNavigate();

  const searchInputRef = useRef<HTMLInputElement>(null);
  const [articulosCache, setArticulosCache] = useState<Map<number, ArticuloSearchResult>>(new Map());
  const [sucursalId, setSucursalId] = useState<number>(orderDetails.proveedor.Id);
  const [articulos, setArticulos] = useState<ArticuloCompra[]>([]);
  const [sucursales, setSucursales] = useState<SucursalItem[]>([]);
  const [tiposEmbalaje, setTiposEmbalaje] = useState<GenericItem[]>([]);
  const [impuestos, setImpuestos] = useState<ImpuestoItem[]>([]);
  const [searchResults, setSearchResults] = useState<ArticuloSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [newArticulo, setNewArticulo] = useState<Partial<ArticuloCompra>>({
    CantidadComprada: 1, PrecioCompra: 0, Total: 0, IdSucursal: sucursalId,
  });
  const [totals, setTotals] = useState({ subtotal: 0, impuestos: 0, total: 0 });

  const { getAll: getTiposEmbalaje } = useMaintenanceApi("tipos-embalaje");
  const impuestosApi = useImpuestosApi();
  const sucursalesApi = useSucursalesApi();
  const articulosApi = useArticulosApi();

  useEffect(() => { loadCatalogos(); }, []);

  const loadCatalogos = async () => {
    try {
      const [s, e, i] = await Promise.all([sucursalesApi.getAll(), getTiposEmbalaje(), impuestosApi.getAll()]);
      setSucursales(s); setTiposEmbalaje(e); setImpuestos(i);
    } catch { setErrorMessage("Error al cargar catálogos"); }
  };

  const refreshArticuloData = async (id: number): Promise<ArticuloSearchResult | null> => {
    try {
      const r = await articulosApi.findOne(id);
      const mapped: ArticuloSearchResult = {
        Id: r.Id, EAN: r.EAN, Nombre: r.Nombre, Descripcion: r.Descripcion || "",
        PrecioCompra: r.PrecioCompra, IdImpuesto: r.IdImpuesto,
        articuloNombre: `${r.EAN} - ${r.Nombre}`,
      };
      setArticulosCache((prev) => new Map(prev).set(id, mapped));
      return mapped;
    } catch { return null; }
  };

  const validateCantidad = (v: number) => v > 0;
  const validatePrecio = (v: number) => v >= 0;

  const calculateTotals = (items: ArticuloCompra[]) => {
    const subtotal = items.reduce((s, i) => s + (i.Total || 0), 0);
    setTotals({ subtotal, impuestos: 0, total: subtotal });
  };

  const handleArticuloChange = (field: keyof ArticuloCompra, value: any) => {
    const updated = { ...newArticulo, [field]: value };
    if (field === "CantidadComprada" || field === "PrecioCompra") {
      const c = field === "CantidadComprada" ? value : newArticulo.CantidadComprada || 0;
      const pr = field === "PrecioCompra" ? value : newArticulo.PrecioCompra || 0;
      if (validateCantidad(c) && validatePrecio(pr)) updated.Total = c * pr;
    }
    setNewArticulo(updated);
  };

  const handleSearch = async (term: string) => {
    if (!term || term.length < 2) { setSearchResults([]); return; }
    setIsSearching(true);
    try {
      const results = await articulosApi.searchArticulos(term);
      results.forEach((a) => setArticulosCache((prev) => new Map(prev).set(a.Id, a)));
      setSearchResults(results);
    } catch { setSearchResults([]); }
    finally { setIsSearching(false); }
  };

  const handleArticuloSelect = async (art: ArticuloSearchResult | null) => {
    if (!art) return;
    const a = await refreshArticuloData(art.Id);
    if (a) {
      const c = newArticulo.CantidadComprada || 1;
      setNewArticulo((prev) => ({
        ...prev, IdArticulo: a.Id, CantidadComprada: c, PrecioCompra: a.PrecioCompra,
        Total: c * a.PrecioCompra, FechaCompra: new Date().toISOString().slice(0, 19).replace("T", " "),
        articulo: { Nombre: a.Nombre, Descripcion: a.Descripcion, EAN: a.EAN, IdImpuesto: a.IdImpuesto, PrecioCompra: a.PrecioCompra },
      }));
    }
  };

  const handleAddArticulo = async () => {
    if (!newArticulo.IdArticulo) return;
    const a = await refreshArticuloData(newArticulo.IdArticulo);
    if (a) {
      const item = {
        ...newArticulo, PrecioCompra: a.PrecioCompra, IdSucursal: sucursalId,
        FechaCompra: new Date().toISOString().slice(0, 19).replace("T", " "),
        articulo: { ...newArticulo.articulo, PrecioCompra: a.PrecioCompra },
      } as ArticuloCompra;
      setArticulos((prev) => [...prev, item]);
      calculateTotals([...articulos, item]);
      setNewArticulo({ CantidadComprada: 1, PrecioCompra: 0, Total: 0, IdSucursal: sucursalId });
    }
  };

  const handleDeleteArticulo = (i: number) => {
    const updated = articulos.filter((_, idx) => idx !== i);
    setArticulos(updated); calculateTotals(updated);
  };

  const getPriceUpdates = (): PriceUpdate[] =>
    articulos.filter((a) => a.PrecioCompra !== a.articulo?.PrecioCompra).map((a) => ({ Id: a.IdArticulo, PrecioCompra: a.PrecioCompra }));

  const handleSave = async () => {
    if (articulos.length === 0) { setErrorMessage("Agregue al menos un artículo"); return; }
    if (!sucursalId) { setErrorMessage("Seleccione una sucursal"); return; }
    if (getPriceUpdates().length > 0) { setShowConfirmDialog(true); return; }
    await saveOrder();
  };

  const saveOrder = async () => {
    setIsSaving(true);
    try {
      const data: OrderData = {
        orden: {
          IdCompra: idCompra,
          articulosCompra: articulos.map((a) => ({
            IdArticulo: a.IdArticulo, IdSucursal: sucursalId,
            CantidadComprada: a.CantidadComprada, PrecioCompra: a.PrecioCompra,
            FechaCompra: new Date().toISOString().slice(0, 19).replace("T", " "),
          })),
          subtotal: totals.subtotal, impuestos: 0, total: totals.total,
        },
        actualizacionesPrecios: getPriceUpdates(),
      };
      await onSave(data);
      await Swal.fire({ icon: "success", title: "¡Guardado!", text: "Orden guardada correctamente", timer: 1800, showConfirmButton: false });
      navigate("/compras");
    } catch (e) {
      toast.error("Error");
    } finally { setIsSaving(false); setShowConfirmDialog(false); }
  };

  return (
    <>
    <Fade in timeout={350}>
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          {/* Header del proveedor */}
          <HeaderSection empresa={orderDetails.empresa} proveedor={orderDetails.proveedor} />

          {/* Sucursal */}
          <Box sx={{ my: 3 }}>
            <SucursalSection sucursalId={sucursalId} sucursales={sucursales} onSucursalChange={setSucursalId} />
          </Box>

          {/* Grid de artículos */}
          <ArticulosGrid
            articulos={articulos} newArticulo={newArticulo} tiposEmbalaje={tiposEmbalaje}
            searchResults={searchResults} isSearching={isSearching} totals={totals}
            onArticuloChange={handleArticuloChange} onArticuloSelect={handleArticuloSelect}
            onSearch={handleSearch} onAdd={handleAddArticulo} onDelete={handleDeleteArticulo}
            validateCantidad={validateCantidad} validatePrecio={validatePrecio}
            orderNumber={orderDetails.OrdenCompra}
          />

          {/* Botones */}
          <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 3 }}>
            <Button
              onClick={() => navigate("/compras")}
              startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
              sx={{ color: p.textS, fontSize: "0.84rem", "&:hover": { bgcolor: darkMode ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)" } }}
            >
              Volver
            </Button>
            <Button
              variant="contained" disableElevation
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: 16 }} />}
              onClick={handleSave}
              disabled={isSaving || articulos.length === 0}
              sx={{
                bgcolor: p.accent, color: "#FFF", px: 3, fontSize: "0.84rem",
                "&:hover": { bgcolor: darkMode ? "#6BAFDB" : "#1B6FC2" },
              }}
            >
              {isSaving ? "Guardando..." : "Guardar Orden"}
            </Button>
          </Stack>
        </Box>
      </Fade>

      <PriceUpdateDialog
        open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}
        onConfirm={saveOrder} priceUpdates={getPriceUpdates()} articulos={articulos} isSaving={isSaving}
      />
      <NotificationSnackbars
        successMessage="Orden guardada" errorMessage={errorMessage}
        showSuccess={showSuccessMessage} onSuccessClose={() => setShowSuccessMessage(false)}
        onErrorClose={() => setErrorMessage(null)}
      />
    </>
  );
};

export default PurchaseOrderDetails;
